import { MenuDivider, MenuItem, MenuList } from "@fluentui/react-components";
import React, { useMemo, ReactElement } from "react";
import { useNavigate } from "react-router";

import { useAppRouteParams } from "../AppRoutes";
import { getFileVersionViews } from "../api/files";
import { useAppNotificationManager } from "../components/AppProvider/AppNotificationProvider";

import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { generateId } from "@/utils/generateId";

/**
 * @description 各種データ一覧画面で利用するcontextItemsを生成
 */
export const useContextMenuItemsVersionsMenu = (): ReactElement => {
  const navigate = useNavigate();
  const { hasPermissionWithId, hasPermission } = usePermissions();
  const hasVersionsWritePermission = hasPermission("versions", "write");
  const { organizations } = useAppOrgsAndInvitedOrgs();
  const { organizationId, versionId } = useAppRouteParams();
  const { versionCategories } = useVersionCategories();
  const organization = organizations.find(
    (org) => org.org_id === organizationId
  );
  const hasAnyVersionWritePermission = useMemo(
    () =>
      hasVersionsWritePermission ||
      (versionCategories?.length
        ? versionCategories.some(({ id }) =>
            hasPermissionWithId("versions_version_category", "write", id)
          )
        : false),
    [hasVersionsWritePermission, versionCategories, hasPermissionWithId]
  );
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );

  const isDisableEdit = useMemo(() => {
    if (!hasAnyVersionWritePermission) return true;
    if (!hasVersionsMetaWritePermission) return true;
    if (organizationId && organization?.is_sheet_enabled === true) return false;
    return true;
  }, [
    hasAnyVersionWritePermission,
    hasVersionsMetaWritePermission,
    organizationId,
    organization,
  ]);

  return useMemo(
    () => (
      <MenuList>
        <MenuItem
          key="k1"
          onClick={() => {
            navigate(`/organizations/${organizationId}/versions/create`);
          }}
          disabled={isDisableEdit}
        >
          新規バージョンを作成
        </MenuItem>
        <MenuItem
          key="k2"
          onClick={() => {
            navigate(
              `/organizations/${organizationId}/versions/upload_sheet_schema`
            );
          }}
          disabled={isDisableEdit}
        >
          設定ファイルから新規バージョン作成
        </MenuItem>
      </MenuList>
    ),
    [organizationId, versionId, isDisableEdit]
  );
};

export const useContextMenuItemsViewsMenu = ({
  isDisabled = false,
}: {
  isDisabled?: boolean;
}): ReactElement => {
  const navigate = useNavigate();
  const { organizationId, versionId } = useAppRouteParams();
  const { hasPermission } = usePermissions();
  const hasVersionsWritePermission = hasPermission("versions", "write");
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );

  const {
    showInfoNotification,
    showSuccessNotification,
    showErrorNotification,
  } = useAppNotificationManager();
  const isDisableMetadataEdit =
    isDisabled ||
    !hasVersionsWritePermission ||
    !hasVersionsMetaWritePermission;

  return useMemo(
    () => (
      <MenuList>
        <MenuItem
          key="i1"
          onClick={async () => {
            const newViewId = generateId();
            navigate(
              `/organizations/${organizationId}/versions/${versionId}/views/${newViewId}/edit`
            );
          }}
          disabled={isDisableMetadataEdit}
        >
          新規ビューを作成
        </MenuItem>
        <MenuDivider />
        <MenuItem
          key="i2"
          onClick={async () => {
            try {
              showInfoNotification("設定ファイルのダウンロードを実行中...");
              await getFileVersionViews({
                organizationId,
                versionId,
                dataType: "yaml",
              });
              showSuccessNotification(
                "設定ファイルのダウンロードに成功しました"
              );
            } catch (error) {
              showErrorNotification(
                "設定ファイルのダウンロードに失敗しました",
                error
              );
            }
          }}
          disabled={isDisabled}
        >
          設定ファイルを一括ダウンロード
        </MenuItem>
        <MenuItem
          key="i3"
          onClick={() => {
            navigate(
              `/organizations/${organizationId}/versions/${versionId}/upload_views`
            );
          }}
          disabled={isDisableMetadataEdit}
        >
          設定ファイルを一括アップロード
        </MenuItem>
      </MenuList>
    ),
    [versionId, organizationId, isDisabled, isDisableMetadataEdit]
  );
};
